<template>
  <div class="title-block">
    <span v-if="data.length > 1">{{ $t("app.modals.deleteParticipant.subtitle.bulk", { count: data.length }) }}</span>
    <i18n-t tag="span" keypath="app.modals.deleteParticipant.subtitle.solo" v-else>
      <template #talent>
        <span class="red-color bold" v-safe-html="candidateWording" />
      </template>
    </i18n-t>
  </div>
  <p class="under-text">
    {{ $t("app.modals.deleteParticipant.text") }}
  </p>
  <div class="btn-container">
    <Button class="secondary outline" :label="t('app.modals.deleteParticipant.buttons.cancel')" @click="emits('close')" />
    <Button class="red" :label="$t('app.modals.deleteParticipant.buttons.confirm', { count: data.length })" :loading="state.btnIsLoading" @click="deleteParticipants()" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, inject } from "vue";
import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";
import { getError } from "@/composables/error.js";
import { useDeleteParticipations } from "@/queries/participant.query";
import { Participant } from "@/types/participant";

const { mutateAsync: deleteParticipations } = useDeleteParticipations();

const toast = useToast();
const route = useRoute();
const { t } = useI18n();

const state = reactive({
  btnIsLoading: false,
});

const emits = defineEmits(["close", "confirm", "confirmBulk"]);

const props = defineProps<{
  data: Participant[],
}>();

const candidateWording = computed(() => {
  let candidate: string;

  if (props.data[0].candidate_id) {
    candidate = `${props.data[0].candidate.first_name} ${props.data[0].candidate.last_name}`;
  } else if (props.data[0].pending_first_name && props.data[0].pending_last_name) {
    candidate = `${props.data[0].pending_first_name} ${props.data[0].pending_last_name}`;
  } else {
    candidate = props.data[0].pending_email;
  }

  return candidate;
});

async function deleteParticipants() {
  state.btnIsLoading = true;

  await deleteParticipations({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string,
      campaignId: route.params.campaignId as string,
    },
    participantIds: props.data.map((elt) => elt?.participant_id as string),
  }, {
    onSuccess: () => {
      toast.success(props.data.length > 1 ? t("app.modals.deleteParticipant.toasts.success.bulk") : t("app.modals.deleteParticipant.toasts.success.solo"));
      if (props.data.length > 1) emits("confirmBulk");
    },
    onError: (error) => {
      toast.error(error);
    }
  });

  emits("close");

  state.btnIsLoading = false;
}
</script>
